import React, { useState } from 'react';
import { formatCurrency } from './shared/formatCompact';
import TooltipBadge from './shared/TooltipBadge';

const CharacterStats = ({ character, crownPulse = false }) => {
  const [showEquipment, setShowEquipment] = useState(false);
  const [showSeeds, setShowSeeds] = useState(false);

  if (!character) return null;

  // ── Helpers ──
  const calculateLevel = (xp) => Math.floor((Math.sqrt(1 + 8 * Math.floor(xp / 1000)) - 1) / 2) + 1;
  const calculateModifier = (stat) => Math.floor((stat - 10) / 2);
  const fmtMod = (stat) => { const m = calculateModifier(stat); return m >= 0 ? `+${m}` : `${m}`; };
  const seedLevel = (xp) => Math.floor(Math.sqrt((xp || 0) / 100));
  const xpForLevel = (lvl) => Math.ceil(((2 * lvl - 1) ** 2 - 1) / 8) * 1000;

  const parseJSON = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val !== 'string') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  // ── Derived Data ──
  const level = calculateLevel(character.experience);
  const xpCurrent = character.experience;
  const xpThisLevel = xpForLevel(level);
  const xpNextLevel = xpForLevel(level + 1);
  const xpIntoLevel = xpCurrent - xpThisLevel;
  const xpNeeded = xpNextLevel - xpThisLevel;
  const xpPct = xpNeeded > 0 ? (xpIntoLevel / xpNeeded) * 100 : 100;

  const currentHp = character.max_hp - character.damage;
  const currentMp = character.max_mp - character.used_mp;
  const hpPct = character.max_hp > 0 ? (currentHp / character.max_hp) * 100 : 0;
  const mpPct = character.max_mp > 0 ? (currentMp / character.max_mp) * 100 : 0;

  const inventory = parseJSON(character.inventory, []);
  const equipment = parseJSON(character.equipment, {});
  const petData = parseJSON(character.pet_data, null);
  const activeBuffs = parseJSON(character.active_buffs, []);
  const seeds = parseJSON(character.seeds, {});
  const skillTree = parseJSON(character.skill_tree, {});

  const usedSlots = inventory.length;
  const capacity = 20 + (equipment.backpack?.size || 0);
  const capacityPct = capacity > 0 ? (usedSlots / capacity) * 100 : 0;
  const capacityColor = capacityPct >= 100 ? '#e74c3c' : capacityPct >= 80 ? '#f39c12' : '#2ecc71';

  // Skill points spent
  const skillPointsSpent = Object.values(skillTree).reduce((sum, v) => sum + (v || 0), 0);

  // Seeds that have XP > 0
  const activeSeeds = Object.entries(seeds).filter(([, xp]) => xp > 0).sort((a, b) => b[1] - a[1]);

  // Equipment slots for display
  const equipSlots = [
    { key: 'weapon', label: '🗡️ Weapon' },
    { key: 'helm', label: '⛑️ Helm' },
    { key: 'breastplate', label: '🛡️ Chest' },
    { key: 'pauldrons_left', label: '🔩 Pauldron L' },
    { key: 'pauldrons_right', label: '🔩 Pauldron R' },
    { key: 'gloves_left', label: '🧤 Glove L' },
    { key: 'gloves_right', label: '🧤 Glove R' },
    { key: 'grieves_left', label: '🥾 Greave L' },
    { key: 'grieves_right', label: '🥾 Greave R' },
    { key: 'belt', label: '📿 Belt' },    { key: 'ring_left', label: '💍 Ring L' },
    { key: 'ring_right', label: '💍 Ring R' },    { key: 'backpack', label: '🎒 Backpack' },
  ];

  // Area name cleanup
  const areaName = (character.current_area || 'unknown')
    .replace('.dat', '')
    .replace(/(\d+)$/, ' $1')
    .replace(/^\w/, c => c.toUpperCase());

  // ── Styles ──
  const sectionStyle = {
    marginTop: '0.4rem',
    paddingTop: '0.4rem',
    borderTop: '1px solid #2a2a3a',
  };

  const collapseHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '0.2rem 0',
    userSelect: 'none',
  };

  const miniBarBg = {
    width: '100%',
    height: '12px',
    background: '#1a1a20',
    border: '1px solid #2a2a3a',
    borderRadius: '3px',
    overflow: 'hidden',
    margin: '0.1rem 0 0.25rem 0',
    position: 'relative',
  };

  const seedBarBg = {
    width: '100%',
    height: '6px',
    background: '#1a1a20',
    borderRadius: '2px',
    overflow: 'hidden',
    marginTop: '1px',
  };

  return (
    <div className="character-stats">
      {/* ═══ IDENTITY HEADER ═══ */}
      <h3 style={{ marginBottom: '0.15rem' }}>{character.name}</h3>
      <div style={{ textAlign: 'center', fontSize: '0.72rem', color: '#8a8878', marginBottom: '0.4rem' }}>
        Level {level} {character.race} {character.advanced_class || character.class}
        {(character.daily_streak || 0) >= 2 && (
          <span
            title={`${character.daily_streak}-day login streak — log in tomorrow to keep it going`}
            style={{
              marginLeft: '0.4rem',
              padding: '0 0.35rem',
              borderRadius: '8px',
              background: 'rgba(201, 168, 76, 0.12)',
              border: '1px solid rgba(201, 168, 76, 0.35)',
              color: '#c9a84c',
              fontSize: '0.66rem',
              whiteSpace: 'nowrap',
            }}
          >
            🔥 {character.daily_streak}
          </span>
        )}
      </div>

      {/* ═══ VITAL BARS ═══ */}
      {/* HP */}
      <div className="stat-row" style={{ marginBottom: '0.05rem' }}>
        <span className="label">❤️ HP</span>
        <span className="value" style={{ color: hpPct < 25 ? '#e74c3c' : hpPct < 50 ? '#f39c12' : '#e05050' }}>
          {currentHp}/{character.max_hp}
        </span>
      </div>
      <div style={miniBarBg}>
        <div className="progress-bar-fill hp-bar" style={{ width: `${hpPct}%`, height: '100%' }} />
      </div>

      {/* MP */}
      {character.max_mp > 0 && (
        <>
          <div className="stat-row" style={{ marginBottom: '0.05rem' }}>
            <span className="label">💧 MP</span>
            <span className="value" style={{ color: '#5080e0' }}>{currentMp}/{character.max_mp}</span>
          </div>
          <div style={miniBarBg}>
            <div className="progress-bar-fill mp-bar" style={{ width: `${mpPct}%`, height: '100%' }} />
          </div>
        </>
      )}

      {/* XP */}
      <div className="stat-row" style={{ marginBottom: '0.05rem' }}>
        <span className="label">✨ XP</span>
        <span className="value" style={{ color: '#bb6bd9', fontSize: '0.72rem' }}>
          {formatCurrency(xpIntoLevel)}/{formatCurrency(xpNeeded)}
        </span>
      </div>
      <div style={miniBarBg}>
        <div className="progress-bar-fill xp-bar" style={{ width: `${xpPct}%`, height: '100%' }} />
        <span className="progress-bar-text" style={{ fontSize: '0.6rem' }}>
          Lv.{level} → {level + 1}
        </span>
      </div>
      {/* v642.10: Rested XP badge — only shown when the player has a
          pool accumulated. The TooltipBadge explains the doubling rule. */}
      {(character.rested_xp_pct || 0) > 0 && (
        <div style={{
          fontSize: '0.66rem',
          color: '#aab8c8',
          marginTop: '-0.1rem',
          marginBottom: '0.25rem',
          display: 'flex',
          alignItems: 'center',
        }}>
          <span>😴 {Math.round(character.rested_xp_pct)}% rested</span>
          <TooltipBadge id="xp.rested" size={11} />
        </div>
      )}

      {/* ═══ GOLD, CROWNS & LOCATION ═══ */}
      <div style={sectionStyle}>
        <div className="stat-row">
          <span className="label">💰 Gold</span>
          <span className="value" style={{ color: '#c9a84c' }}>{formatCurrency(character.gold || 0)}</span>
        </div>
        {character.crowns > 0 && (
          <div className={`stat-row${crownPulse ? ' crown-pulse-row' : ''}`}>
            <span className="label">👑 Crowns</span>
            <span className="value" style={{ color: '#ffd700' }}>{formatCurrency(character.crowns || 0)}</span>
          </div>
        )}
        <div className="stat-row">
          <span className="label">📍 Area</span>
          <span className="value" style={{ fontSize: '0.72rem', color: '#8a8878' }}>{areaName}</span>
        </div>
      </div>

      {/* ═══ COMBAT STATS ═══ */}
      <div style={sectionStyle}>
        <div style={{ fontSize: '0.7rem', color: '#c9a84c', fontFamily: 'var(--font-ui)', marginBottom: '0.25rem', letterSpacing: '0.5px' }}>
          Combat
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1rem 0.5rem' }}>
          <div className="stat-row" style={{ margin: 0 }}>
            <span className="label">⚔️ ATT</span>
            <span className="value">{character.att || 0}</span>
          </div>
          <div className="stat-row" style={{ margin: 0 }}>
            <span className="label">🛡️ DEF</span>
            <span className="value">{character.def || 0}</span>
          </div>
          <div className="stat-row" style={{ margin: 0 }}>
            <span className="label">🔰 DR</span>
            <span className="value">{character.dr || 0}</span>
          </div>
          {equipment.weapon && (
            <div className="stat-row" style={{ margin: 0 }}>
              <span className="label">🗡️</span>
              <span className="value" style={{ fontSize: '0.68rem', color: '#aaa' }}>
                {equipment.weapon.damage || '—'}
              </span>
            </div>
          )}
        </div>
        {equipment.weapon && (
          <div style={{ fontSize: '0.68rem', color: '#888', marginTop: '0.15rem' }}>
            {equipment.weapon.name}{equipment.weapon.magic ? ' ✦' : ''}
          </div>
        )}
      </div>

      {/* ═══ ATTRIBUTES ═══ */}
      <div style={sectionStyle}>
        <div style={{ fontSize: '0.7rem', color: '#c9a84c', fontFamily: 'var(--font-ui)', marginBottom: '0.25rem', letterSpacing: '0.5px' }}>
          Attributes
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1rem 0.3rem' }}>
          {[
            { key: 'strength', abbr: 'STR', color: '#e74c3c' },
            { key: 'agility', abbr: 'AGL', color: '#2ecc71' },
            { key: 'vitality', abbr: 'VIT', color: '#e67e22' },
            { key: 'mentality', abbr: 'MEN', color: '#3498db' },
            { key: 'charisma', abbr: 'CHA', color: '#9b59b6' },
          ].map(({ key, abbr, color }) => (
            <div key={key} className="stat-row" style={{ margin: 0 }}>
              <span className="label" style={{ color }}>{abbr}</span>
              <span className="value">
                {character[key]}{' '}
                <span style={{ fontSize: '0.65rem', color: '#666' }}>({fmtMod(character[key])})</span>
              </span>
            </div>
          ))}
          {/* Stat points available */}
          {(character.stat_points > 0) && (
            <div className="stat-row" style={{ margin: 0 }}>
              <span className="label" style={{ color: '#f1c40f' }}>PTS</span>
              <span className="value" style={{ color: '#f1c40f' }}>{character.stat_points}</span>
            </div>
          )}
        </div>
      </div>

      {/* ═══ GUILD ═══ */}
      {character.guild_name && (
        <div style={sectionStyle}>
          <div className="stat-row">
            <span className="label">⚜️ Guild</span>
            <span className="value" style={{ fontSize: '0.72rem' }}>
              [{character.guild_tag}] {character.guild_name}
            </span>
          </div>
          {character.guild_rank && (
            <div className="stat-row">
              <span className="label">Rank</span>
              <span className="value" style={{ fontSize: '0.72rem', color: '#888' }}>{character.guild_rank}</span>
            </div>
          )}
        </div>
      )}

      {/* ═══ EQUIPMENT (collapsible) ═══ */}
      <div style={sectionStyle}>
        <div style={collapseHeaderStyle} onClick={() => setShowEquipment(!showEquipment)}>
          <span style={{ fontSize: '0.7rem', color: '#c9a84c', fontFamily: 'var(--font-ui)', letterSpacing: '0.5px' }}>
            Equipment
          </span>
          <span style={{ fontSize: '0.65rem', color: '#666' }}>{showEquipment ? '▼' : '▶'}</span>
        </div>
        {showEquipment && (
          <div style={{ fontSize: '0.7rem', marginTop: '0.15rem' }}>
            {equipSlots.map(({ key, label }) => {
              const item = equipment[key];
              return (
                <div key={key} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.12rem 0', borderBottom: '1px solid #1a1a20',
                }}>
                  <span style={{ color: '#666', fontSize: '0.65rem' }}>{label}</span>
                  <span style={{
                    color: item ? (item.magic ? '#f1c40f' : '#ccc') : '#333',
                    fontSize: '0.65rem', maxWidth: '55%', textAlign: 'right',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item ? item.name : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ ACTIVE BUFFS ═══ */}
      {activeBuffs.length > 0 && (
        <div style={sectionStyle}>
          <div style={{ fontSize: '0.7rem', color: '#f1c40f', fontFamily: 'var(--font-ui)', marginBottom: '0.15rem' }}>
            ⚗️ Active Buffs
          </div>
          {activeBuffs.map((buff, idx) => {
            const label = buff.type === 'att' ? `+${buff.value} ATT`
              : buff.type === 'def' ? `+${buff.value} DEF`
              : buff.type === 'fire_damage' ? `+${buff.value} 🔥 Fire`
              : buff.type === 'ice_damage' ? `+${buff.value} ❄️ Ice`
              : `+${buff.value} ${buff.type}`;
            return (
              <div key={idx} className="stat-row" style={{ fontSize: '0.7rem' }}>
                <span className="label" style={{ color: '#f39c12' }}>{buff.name || 'Buff'}</span>
                <span className="value" style={{ color: '#2ecc71', fontSize: '0.68rem' }}>{label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ SEEDS / MAGIC (collapsible) ═══ */}
      {activeSeeds.length > 0 && (
        <div style={sectionStyle}>
          <div style={collapseHeaderStyle} onClick={() => setShowSeeds(!showSeeds)}>
            <span style={{ fontSize: '0.7rem', color: '#c9a84c', fontFamily: 'var(--font-ui)', letterSpacing: '0.5px' }}>
              🔮 Magic Seeds ({activeSeeds.length})
            </span>
            <span style={{ fontSize: '0.65rem', color: '#666' }}>{showSeeds ? '▼' : '▶'}</span>
          </div>
          {showSeeds && (
            <div style={{ marginTop: '0.15rem' }}>
              {activeSeeds.map(([name, xp]) => {
                const lvl = seedLevel(xp);
                const nextXp = (lvl + 1) * (lvl + 1) * 100;
                const prevXp = lvl * lvl * 100;
                const pct = nextXp > prevXp ? ((xp - prevXp) / (nextXp - prevXp)) * 100 : 100;
                return (
                  <div key={name} style={{ marginBottom: '0.2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem' }}>
                      <span style={{ color: '#aaa' }}>{name}</span>
                      <span style={{ color: '#888' }}>Lv.{lvl}</span>
                    </div>
                    <div style={seedBarBg}>
                      <div style={{
                        width: `${Math.min(pct, 100)}%`, height: '100%',
                        background: 'linear-gradient(90deg, #8e44ad, #bb6bd9)',
                        borderRadius: '2px',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ SKILL TREE ═══ */}
      {skillPointsSpent > 0 && (
        <div style={sectionStyle}>
          <div className="stat-row">
            <span className="label" style={{ fontSize: '0.7rem' }}>🌳 Skill Points</span>
            <span className="value" style={{ fontSize: '0.7rem', color: '#2ecc71' }}>{skillPointsSpent} spent</span>
          </div>
        </div>
      )}

      {/* ═══ BACKPACK ═══ */}
      <div style={sectionStyle}>
        <div className="stat-row">
          <span className="label">📦 Backpack</span>
          <span className="value" style={{ color: capacityColor }}>{usedSlots}/{capacity}</span>
        </div>
        <div style={{ ...miniBarBg, height: '8px' }}>
          <div style={{
            width: `${Math.min(capacityPct, 100)}%`, height: '100%',
            backgroundColor: capacityColor, borderRadius: '3px',
          }} />
        </div>
      </div>

      {/* ═══ PET ═══ */}
      {petData && (
        <div style={sectionStyle}>
          <div className="stat-row">
            <span className="label">🐾 Pet</span>
            <span className="value" style={{ color: '#f39c12', fontSize: '0.75rem' }}>
              {petData.name} <span style={{ color: '#888', fontSize: '0.65rem' }}>Lv.{petData.level}</span>
            </span>
          </div>
          <div style={miniBarBg}>
            <div style={{
              width: `${petData.hp > 0 ? (petData.currentHp / petData.hp) * 100 : 0}%`,
              height: '100%', backgroundColor: '#f39c12', borderRadius: '3px',
            }} />
          </div>
          <div style={{ fontSize: '0.65rem', color: '#666', display: 'flex', gap: '0.5rem' }}>
            <span>HP {petData.currentHp}/{petData.hp}</span>
            <span>ATT {petData.att}</span>
            <span>DEF {petData.def}</span>
            <span>DR {petData.dr || 0}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterStats;
